import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getWebsiteSpeed } from '@/lib/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import {
  Gauge, DeviceMobile, Desktop, ArrowUp, ArrowDown, Clock, Warning, CheckCircle, CaretUp, CaretDown, X
} from '@phosphor-icons/react';

const scoreColor = (v) => v >= 90 ? '#34C759' : v >= 50 ? '#FFB800' : '#FF3B30';
const scoreLabel = (v) => v >= 90 ? 'Good' : v >= 50 ? 'Needs Work' : 'Poor';
const lcpColor = (v) => v <= 2.5 ? '#34C759' : v <= 4 ? '#FFB800' : '#FF3B30';
const clsColor = (v) => v <= 0.1 ? '#34C759' : v <= 0.25 ? '#FFB800' : '#FF3B30';

function parseDate(str) {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function formatShortDate(str) {
  const d = parseDate(str);
  if (!d) return str;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

export default function WebsiteSpeedPage() {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const loadData = useCallback(async () => {
    try {
      const res = await getWebsiteSpeed();
      setRawData(res.data.data || []);
    } catch (err) { console.error('Speed data fetch error:', err.message); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Split into mobile/desktop
  const mobile = useMemo(() => rawData.filter(r => r.device === 'Mobile'), [rawData]);
  const desktop = useMemo(() => rawData.filter(r => r.device === 'Desktop'), [rawData]);

  // KPI stats
  const stats = useMemo(() => {
    const mScores = mobile.map(r => r.performance).filter(Boolean);
    const dScores = desktop.map(r => r.performance).filter(Boolean);
    const all = [...mScores, ...dScores];
    const avgM = mScores.length ? Math.round(mScores.reduce((a, b) => a + b, 0) / mScores.length) : 0;
    const avgD = dScores.length ? Math.round(dScores.reduce((a, b) => a + b, 0) / dScores.length) : 0;
    const min = all.length ? Math.min(...all) : 0;
    const max = all.length ? Math.max(...all) : 0;
    const lastDate = rawData.length ? rawData[rawData.length - 1].date : null;
    return { avgM, avgD, min, max, lastDate };
  }, [mobile, desktop, rawData]);

  // Chart data — merge mobile+desktop by date
  const chartData = useMemo(() => {
    const dateMap = {};
    rawData.forEach(r => {
      const d = parseDate(r.date);
      if (!d) return;
      const key = d.toISOString().split('T')[0];
      if (!dateMap[key]) dateMap[key] = { date: key };
      if (r.device === 'Mobile') {
        dateMap[key].mobileScore = r.performance;
        dateMap[key].mobileLcp = r.lcp;
        dateMap[key].mobileCls = r.cls;
      } else {
        dateMap[key].desktopScore = r.performance;
        dateMap[key].desktopLcp = r.lcp;
        dateMap[key].desktopCls = r.cls;
      }
    });
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [rawData]);

  // Sorted table data (all rows)
  const tableData = useMemo(() => {
    const sorted = [...rawData].sort((a, b) => {
      let aV = a[sortField], bV = b[sortField];
      if (sortField === 'date') {
        aV = parseDate(aV)?.getTime() || 0;
        bV = parseDate(bV)?.getTime() || 0;
      }
      if (typeof aV === 'string') return sortDir === 'asc' ? aV.localeCompare(bV) : bV.localeCompare(aV);
      return sortDir === 'asc' ? (aV || 0) - (bV || 0) : (bV || 0) - (aV || 0);
    });
    return sorted;
  }, [rawData, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <CaretUp size={10} /> : <CaretDown size={10} />;
  };

  if (loading) return <div className="p-8 text-[#555555]">Loading speed data...</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="website-speed-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Gauge size={22} weight="fill" className="text-[#32B4AC]" />
          <h1 className="font-['Manrope'] text-2xl font-bold tracking-tight text-[#0F0F0F]">Website Speed</h1>
        </div>
        <p className="text-sm text-[#555555]">Performance monitoring for wellversed.in — data from Google PageSpeed Insights</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="speed-kpi-cards">
        <KpiCard icon={DeviceMobile} label="Avg Mobile" value={stats.avgM} color={scoreColor(stats.avgM)} subtitle={scoreLabel(stats.avgM)} />
        <KpiCard icon={Desktop} label="Avg Desktop" value={stats.avgD} color={scoreColor(stats.avgD)} subtitle={scoreLabel(stats.avgD)} />
        <KpiCard icon={ArrowDown} label="Lowest Score" value={stats.min} color={scoreColor(stats.min)} subtitle="All time" />
        <KpiCard icon={ArrowUp} label="Highest Score" value={stats.max} color={scoreColor(stats.max)} subtitle="All time" />
        <KpiCard icon={Clock} label="Last Updated" value={stats.lastDate ? formatShortDate(stats.lastDate) : '-'} isText subtitle={rawData.length + ' entries'} />
      </div>

      {/* Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Score Trend */}
        <div className="border border-[#E8E8E8] rounded-md p-5" data-testid="speed-score-chart">
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#555555] mb-4">Performance Score Trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8E8" />
              <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E8E8E8' }} labelFormatter={formatShortDate} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="mobileScore" name="Mobile" stroke="#FF3B30" strokeWidth={2} dot={{ r: 2.5, fill: '#fff', stroke: '#FF3B30', strokeWidth: 2 }} connectNulls />
              <Line type="monotone" dataKey="desktopScore" name="Desktop" stroke="#32B4AC" strokeWidth={2} dot={{ r: 2.5, fill: '#fff', stroke: '#32B4AC', strokeWidth: 2 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* LCP Trend */}
        <div className="border border-[#E8E8E8] rounded-md p-5" data-testid="speed-lcp-chart">
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#555555] mb-4">LCP Trend (seconds)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8E8" />
              <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E8E8E8' }} labelFormatter={formatShortDate} formatter={(v) => v != null ? [v.toFixed(1) + 's'] : ['-']} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="mobileLcp" name="Mobile LCP" stroke="#FF3B30" strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
              <Line type="monotone" dataKey="desktopLcp" name="Desktop LCP" stroke="#32B4AC" strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* CLS Trend */}
      <div className="border border-[#E8E8E8] rounded-md p-5" data-testid="speed-cls-chart">
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#555555] mb-4">CLS Trend (Cumulative Layout Shift)</h3>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8E8" />
            <XAxis dataKey="date" tickFormatter={formatShortDate} tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E8E8E8' }} labelFormatter={formatShortDate} formatter={(v) => v != null ? [v.toFixed(3)] : ['-']} />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="mobileCls" name="Mobile CLS" stroke="#FF3B30" strokeWidth={2} dot={{ r: 2 }} connectNulls />
            <Line type="monotone" dataKey="desktopCls" name="Desktop CLS" stroke="#32B4AC" strokeWidth={2} dot={{ r: 2 }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Performance Table */}
      <div className="border border-[#E8E8E8] rounded-md overflow-hidden" data-testid="speed-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F4F4F4]">
                {[
                  { key: 'date', label: 'Date' },
                  { key: 'device', label: 'Device' },
                  { key: 'performance', label: 'Score' },
                  { key: 'lcp', label: 'LCP' },
                  { key: 'cls', label: 'CLS' },
                  { key: 'inp', label: 'INP' },
                ].map(col => (
                  <th key={col.key} onClick={() => toggleSort(col.key)} className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555] cursor-pointer select-none">
                    <span className="flex items-center gap-1">{col.label} <SortIcon field={col.key} /></span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, i) => (
                <tr
                  key={i}
                  onClick={() => setSelectedRow(row)}
                  className="border-t border-[#E8E8E8] hover:bg-[#F4F4F4] cursor-pointer transition-colors"
                  data-testid={`speed-row-${i}`}
                >
                  <td className="px-4 py-3 text-xs text-[#0F0F0F]">{formatShortDate(row.date)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${row.device === 'Mobile' ? 'bg-red-50 text-[#FF3B30]' : 'bg-[#32B4AC]/10 text-[#32B4AC]'}`}>
                      {row.device}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: scoreColor(row.performance) + '18', color: scoreColor(row.performance) }}>
                      {row.performance}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {row.lcp != null ? (
                      <span className="text-xs font-medium" style={{ color: lcpColor(row.lcp) }}>{row.lcp.toFixed(1)}s</span>
                    ) : <span className="text-xs text-[#E8E8E8]">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    {row.cls != null ? (
                      <span className="text-xs font-medium" style={{ color: clsColor(row.cls) }}>{row.cls.toFixed(3)}</span>
                    ) : <span className="text-xs text-[#E8E8E8]">-</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#555555]">{row.inp || '-'}</td>
                </tr>
              ))}
              {tableData.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-sm text-[#555555]">No speed data available</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedRow && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSelectedRow(null)} data-testid="speed-detail-modal">
          <div className="bg-white rounded-md border border-[#E8E8E8] max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Manrope'] text-lg font-bold text-[#0F0F0F]">Performance Detail</h3>
              <button onClick={() => setSelectedRow(null)} className="p-1 hover:bg-[#F4F4F4] rounded-md"><X size={16} className="text-[#555555]" /></button>
            </div>
            <div className="space-y-3">
              <DetailRow label="Date" value={formatShortDate(selectedRow.date)} />
              <DetailRow label="Device" value={selectedRow.device} badge badgeColor={selectedRow.device === 'Mobile' ? '#FF3B30' : '#32B4AC'} />
              <DetailRow label="Performance Score" value={selectedRow.performance} badge badgeColor={scoreColor(selectedRow.performance)} suffix={`/ 100 — ${scoreLabel(selectedRow.performance)}`} />
              <DetailRow label="LCP (Largest Contentful Paint)" value={selectedRow.lcp != null ? selectedRow.lcp.toFixed(1) + 's' : '-'} badge badgeColor={selectedRow.lcp != null ? lcpColor(selectedRow.lcp) : '#555'} suffix={selectedRow.lcp != null ? (selectedRow.lcp <= 2.5 ? 'Good' : selectedRow.lcp <= 4 ? 'Needs Work' : 'Poor') : ''} />
              <DetailRow label="CLS (Cumulative Layout Shift)" value={selectedRow.cls != null ? selectedRow.cls.toFixed(3) : '-'} badge badgeColor={selectedRow.cls != null ? clsColor(selectedRow.cls) : '#555'} suffix={selectedRow.cls != null ? (selectedRow.cls <= 0.1 ? 'Good' : selectedRow.cls <= 0.25 ? 'Needs Work' : 'Poor') : ''} />
              <DetailRow label="INP (Interaction to Next Paint)" value={selectedRow.inp || 'N/A'} />
              <DetailRow label="URL" value={selectedRow.url || '-'} />

              {/* Contextual alert */}
              {selectedRow.performance < 50 && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 mt-2">
                  <Warning size={14} className="text-[#FF3B30] mt-0.5 shrink-0" />
                  <p className="text-xs text-red-800">Performance score is below 50 — this indicates significant issues affecting user experience.</p>
                </div>
              )}
              {selectedRow.performance >= 50 && selectedRow.performance < 90 && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 mt-2">
                  <Warning size={14} className="text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800">Performance needs improvement. Target a score of 90+ for optimal user experience.</p>
                </div>
              )}
              {selectedRow.performance >= 90 && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-green-50 border border-green-200 mt-2">
                  <CheckCircle size={14} className="text-[#34C759] mt-0.5 shrink-0" />
                  <p className="text-xs text-green-800">Great performance! Score is in the green zone.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, subtitle, isText }) {
  return (
    <div className="border border-[#E8E8E8] rounded-md p-4 hover:border-[#1A1A1A] transition-colors" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color: color || '#555555' }} />
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#555555]">{label}</span>
      </div>
      <p className={`font-['Manrope'] font-bold text-[#0F0F0F] ${isText ? 'text-sm' : 'text-2xl'}`} style={!isText && color ? { color } : {}}>
        {value}
      </p>
      {subtitle && <p className="text-[10px] text-[#555555] mt-0.5">{subtitle}</p>}
    </div>
  );
}

function DetailRow({ label, value, badge, badgeColor, suffix }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-[#F4F4F4] last:border-0">
      <span className="text-xs text-[#555555]">{label}</span>
      <div className="flex items-center gap-1.5">
        {badge ? (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: badgeColor + '18', color: badgeColor }}>{value}</span>
        ) : (
          <span className="text-xs font-medium text-[#0F0F0F]">{value}</span>
        )}
        {suffix && <span className="text-[10px] text-[#555555]">{suffix}</span>}
      </div>
    </div>
  );
}
